import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BlockButton as UntypedBlockButton } from "../../inJoy-components/Button/BlockButton";
import { IconButton } from "../../inJoy-components/Button/IconButton";
import { Icon } from "../../inJoy-components/Icon/Icon";
import { Input as UntypedInput } from "../../inJoy-components/Input/Input";
import { InfoCard, type InfoCardItem } from "./InfoCard";

const Input = UntypedInput as any;
const BlockButton = UntypedBlockButton as any;
const imgKv = "/img/guest_landing_kv.png";
const imgLogo = "/img/logo_word_white.svg";

interface LandingScreenProps {
	email: string;
	setEmail: (email: string) => void;
	emailError?: string | null;
	onEmailBlur?: () => void;
	onConfirm: () => void;
	isSubmitting: boolean;
	timeLeft?: string;
	tokenTimeLeft?: string;
	onCountdownInView?: (inView: boolean) => void;
}

export const LandingScreen: React.FC<LandingScreenProps> = ({
	email,
	setEmail,
	emailError,
	onEmailBlur,
	onConfirm,
	isSubmitting,
	timeLeft = "5:30",
	tokenTimeLeft = "2:00",
	onCountdownInView,
}) => {
	const countdownRef = useRef<HTMLDivElement>(null);
	const carouselRef = useRef<HTMLDivElement>(null);
	const [cardIndex, setCardIndex] = useState(0);
	const { t } = useTranslation();

	const infoCards: {
		imageSrc: string;
		title: string;
		subtitle?: string;
		items: InfoCardItem[];
	}[] = [
		{
			imageSrc: "/img/event-page/create_event_info_1.png",
			title: t("guest.landing.info_card_1_title"),
			items: [
				{
					label: t("guest.landing.info_card_1_item_1_label"),
					text: t("guest.landing.info_card_1_item_1_text"),
				},
				{
					label: t("guest.landing.info_card_1_item_2_label"),
					text: t("guest.landing.info_card_1_item_2_text"),
				},
				{
					label: t("guest.landing.info_card_1_item_3_label"),
					text: t("guest.landing.info_card_1_item_3_text"),
				},
			],
		},
		{
			imageSrc: "/img/event-page/create_event_info_2.png",
			title: t("guest.landing.info_card_2_title"),
			subtitle: t("guest.landing.info_card_2_subtitle"),
			items: [
				{
					label: t("guest.landing.info_card_2_item_1_label"),
					text: t("guest.landing.info_card_2_item_1_text"),
				},
				{
					label: t("guest.landing.info_card_2_item_2_label"),
					text: t("guest.landing.info_card_2_item_2_text"),
				},
			],
		},
		{
			imageSrc: "/img/event-page/create_event_info_3.png",
			title: t("guest.landing.info_card_3_title"),
			items: [
				{
					label: t("guest.landing.info_card_3_item_1_label"),
					text: t("guest.landing.info_card_3_item_1_text"),
				},
				{
					label: t("guest.landing.info_card_3_item_2_label"),
					text: t("guest.landing.info_card_3_item_2_text"),
				},
				{
					label: t("guest.landing.info_card_3_item_3_label"),
					text: t("guest.landing.info_card_3_item_3_text"),
				},
			],
		},
	];

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (onCountdownInView) {
					onCountdownInView(entry.isIntersecting);
				}
			},
			{
				root: null, // viewport
				threshold: 0, // trigger as soon as even one pixel is visible
				rootMargin: "-100px 0px 0px 0px", // Offset to trigger slightly before it disappears
			},
		);

		if (countdownRef.current) {
			observer.observe(countdownRef.current);
		}

		return () => {
			if (countdownRef.current) {
				observer.unobserve(countdownRef.current);
			}
		};
	}, [onCountdownInView]);

	return (
		<div className="bg-white flex flex-col items-center min-h-screen relative w-full mx-auto">
			{/* Hero Section */}
			<div className="relative w-full h-[844px] shrink-0">
				<img
					alt="Background"
					className="absolute inset-0 w-full h-full object-cover"
					src={imgKv}
				/>

				{/* Hero Content Container */}
				<div className="absolute inset-0 flex flex-col items-center pt-[40px] pb-[40px] px-[20px]">
					{/* Logo */}
					<div className="w-[109px] h-[24px] shrink-0">
						<img alt="inJOY Logo" className="w-full h-full" src={imgLogo} />
					</div>

					{/* Countdown Overlay (Static) */}
					<div
						ref={countdownRef}
						className="mt-[300px] flex flex-col items-center justify-center text-white drop-shadow-md text-center w-full shrink-0"
					>
						<p className="Subtitle2 font-medium mb-1">
							{t("guest.landing.next_game_starts")}
						</p>
						<div className="font-extrabold leading-none flex items-baseline">
							<span className="text-[50px]">{timeLeft.split(":")[0]}</span>
							<span className="text-[40px] px-1">:</span>
							<span className="text-[50px]">{timeLeft.split(":")[1]}</span>
						</div>
					</div>

					{/* Input Container */}
					<div className="mt-[24px] w-full bg-white rounded-lg p-6 shadow-lg flex flex-col gap-5 z-10 shrink-0">
						<p className="H5 text-color_text_grey_5 font-semibold">
							{t("guest.landing.ready_to_play")}
						</p>

						<div className="flex flex-col gap-5 w-full">
							<div className="flex flex-col gap-2 w-full">
								<Input
									label={
										<div className="flex items-center gap-[2px]">
											<span className="text-color_text_grey_5">
												{t("guest.landing.email")}
											</span>
											<span className="text-color_text_negative_3">*</span>
										</div>
									}
									required
									placeholder="example@gmail.com"
									value={email}
									onChange={(e: any) => setEmail(e.target.value)}
									onBlur={onEmailBlur}
									error={emailError}
									hint={!emailError ? t("guest.landing.email_hint") : undefined}
								/>
							</div>
							<div className="flex flex-col gap-3">
								<BlockButton
									type="Primary"
									size="Large"
									width="100%"
									disabled={!email || isSubmitting}
									loading={isSubmitting}
									onClick={onConfirm}
								>
									{t("guest.landing.play_now")}
								</BlockButton>
								<p className="text-xs text-center text-color-Grey500">
									{t("guest.landing.link_expires", {
										time: tokenTimeLeft,
									})}
								</p>
							</div>
						</div>
					</div>

					{/* Spacer to push More Info to bottom */}
					<div className="flex-1" />

					{/* Footer/More Info Hint */}
					<div className="flex flex-col items-center justify-start gap-1 z-20 shrink-0 mt-auto">
						<p className="Body2 text-color_text_grey_5 text-center">
							{t("guest.landing.more_info")}
						</p>
						<div className="w-6 h-6 flex items-center justify-center animate-bounce">
							<Icon
								iconName="arrow_down"
								size="Small"
								color="var(--color_text_grey_5)"
							/>
						</div>
					</div>
				</div>

				{/* Gradient Overlay for Footer readability */}
				<div className="absolute bottom-[0px] left-0 right-0 h-[164px] bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-0" />
			</div>

			{/* Content below fold (Things to know) */}

			<div className="w-full px-[20px] py-[24px] bg-white z-10 flex flex-col gap-[24px]">
				<h2 className="font-medium text-[24px] leading-[1.2] tracking-[-0.5px] text-color_text_grey_5">
					{t("guest.landing.things_to_know")}
				</h2>

				<div
					ref={carouselRef}
					className="flex overflow-x-auto gap-[20px] pb-4 -mx-[20px] px-[20px] scrollbar-hide snap-x snap-mandatory"
				>
					{infoCards.map((card, i) => (
						<InfoCard key={i} {...card} />
					))}
				</div>

				{/* Carousel Navigation */}
				<div className="flex gap-[12px] items-center justify-end w-full">
					<IconButton
						type="Secondary"
						size="Small"
						icon="arrow_left"
						iconSize="Small"
						disabled={cardIndex === 0}
						onClick={() => {
							const next = Math.max(0, cardIndex - 1);
							setCardIndex(next);
							const container = carouselRef.current;
							if (container?.children[next]) {
								(container.children[next] as HTMLElement).scrollIntoView({
									behavior: "smooth",
									block: "nearest",
									inline: "start",
								});
							}
						}}
					/>
					<IconButton
						type="Secondary"
						size="Small"
						icon="arrow_right"
						iconSize="Small"
						disabled={cardIndex === infoCards.length - 1}
						onClick={() => {
							const next = Math.min(infoCards.length - 1, cardIndex + 1);
							setCardIndex(next);
							const container = carouselRef.current;
							if (container?.children[next]) {
								(container.children[next] as HTMLElement).scrollIntoView({
									behavior: "smooth",
									block: "nearest",
									inline: "start",
								});
							}
						}}
					/>
				</div>
			</div>
		</div>
	);
};
